/** Ce que `GET /api/users/:username` et `POST /api/users` rendent (#149). */
export interface OperatorTenantPermission {
  tenantCod: string;
  permissionCode: string;
}

export interface Operator {
  username: string;
  email: string;
  isActive: boolean;
  lastLoginAt: string | null;
  /** Qui l'a enregistre. Nul pour les comptes anterieurs a la regle. */
  createdBy: string | null;
  /** `pending` tant que le compte Keycloak n'existe pas — creation en echec, a rejouer. */
  keycloakStatus: 'provisioned' | 'pending';
  /** Quand Keycloak a confirme l'envoi du mail de definition du mot de passe. */
  invitedAt: string | null;
  permissions: OperatorTenantPermission[];
}

export const CREATE_OPERATOR = 'CREATE_OPERATOR';
export const GRANT_PERMISSIONS = 'GRANT_PERMISSIONS';
export const OPERATOR_SCREEN_PERMISSIONS = [CREATE_OPERATOR, GRANT_PERMISSIONS] as const;

export type OperatorStatus = 'active' | 'inactive' | 'invited' | 'pending' | 'not-invited';

/**
 * Ce que l'ecran affiche comme statut. `isActive` a `false` ne veut dire
 * qu'une chose — suspendu par un humain — et prime sur tout. Ensuite, un
 * compte qui ne s'est jamais connecte est en attente : de son compte
 * Keycloak, de son invitation, ou de sa premiere connexion.
 */
export function operatorStatus(
  o: Pick<Operator, 'isActive' | 'lastLoginAt' | 'keycloakStatus' | 'invitedAt'>,
): OperatorStatus {
  if (!o.isActive) return 'inactive';
  if (o.lastLoginAt) return 'active';
  if (o.keycloakStatus === 'pending') return 'pending';
  if (o.invitedAt) return 'invited';
  return 'not-invited';
}

/**
 * Ce que l'attributeur peut donner, par tenant : ses propres droits, sur
 * les tenants ou il detient GRANT_PERMISSIONS. Sans GRANT sur un tenant,
 * rien n'y est permis.
 */
export function grantableFrom(actor: Pick<Operator, 'permissions'>): Record<string, string[]> {
  const byTenant: Record<string, string[]> = {};
  for (const p of actor.permissions) {
    (byTenant[p.tenantCod] ??= []).push(p.permissionCode);
  }
  return Object.fromEntries(
    Object.entries(byTenant).filter(([, codes]) => codes.includes(GRANT_PERMISSIONS)),
  );
}
